import logger from './logger';

/**
 * Background Task Manager
 * Coordinates between main thread, Service Worker, and IndexedDB
 * Handles registration and monitoring of background tasks
 */

import pixleyDB from './db.js';

class BackgroundTaskManager {
  constructor() {
    this.registration = null;
    this.listeners = new Map();
    this.init();
  }

  /**
   * Initialize the background task manager
   */
  async init() {
    try {
      // Register service worker
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        this.registration = await navigator.serviceWorker.register('/sw.js');
        logger.log('✅ Service Worker registered successfully');

        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage.bind(this));

        // Handle service worker messages for auth token requests
        navigator.serviceWorker.addEventListener('message', event => {
          if (event.data.type === 'GET_AUTH_TOKEN') {
            const token = localStorage.getItem('pixley_auth_token');
            event.ports[0].postMessage({ token });
          }
        });

        // Request notification permission
        await this.requestNotificationPermission();

      } else {
        logger.warn('⚠️ Background sync not supported in this browser');
      }
    } catch (error) {
      logger.error('❌ Failed to initialize background task manager:', error);
    }
  }

  /**
   * Request notification permission from user
   */
  async requestNotificationPermission() {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        logger.log('📢 Notification permission:', permission);
      }
    }
  }

  /**
   * Create a character in the background
   * @param {Object} characterData - Character creation data
   * @returns {Promise<string>} Task ID
   */
  async createCharacterInBackground(characterData) {
    const taskId = `create-character-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Store task in IndexedDB
      await pixleyDB.storePendingTask({
        id: taskId,
        type: 'character_creation',
        userId: characterData.user_id,
        data: {
          userId: characterData.user_id,
          profile_id: characterData.profile_id,
          name: characterData.name,
          handdrawn_image: characterData.handdrawn_image,
          animated_image: characterData.animated_image
        },
        status: 'pending'
      });

      // Register background sync
      if (this.registration && 'sync' in this.registration) {
        await this.registration.sync.register(taskId);
        logger.log('🔄 Background sync registered for:', taskId);
      } else {
        // Fallback: immediate processing
        logger.warn('⚠️ Background sync not available, processing immediately');
        throw new Error('Background sync not supported');
      }

      return taskId;

    } catch (error) {
      logger.error('❌ Failed to create background character task:', error);
      // Clean up failed task
      await pixleyDB.deleteTask(taskId).catch(logger.error);
      throw error;
    }
  }

  /**
   * Create a show in the background (future implementation)
   * @param {Object} showData - Show creation data
   * @returns {Promise<string>} Task ID
   */
  async createShowInBackground(showData) {
    const taskId = `create-show-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    await pixleyDB.storePendingTask({
      id: taskId,
      type: 'show_creation',
      userId: showData.user_id,
      data: showData,
      status: 'pending'
    });

    if (this.registration && 'sync' in this.registration) {
      await this.registration.sync.register(taskId);
    }

    return taskId;
  }

  /**
   * Get all pending tasks for current user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of pending tasks
   */
  async getPendingTasks(userId) {
    return await pixleyDB.getPendingTasksForUser(userId);
  }

  /**
   * Get task status
   * @param {string} taskId - Task ID
   * @returns {Promise<Object|null>} Task object or null
   */
  async getTaskStatus(taskId) {
    return await pixleyDB.getPendingTask(taskId);
  }

  /**
   * Add listener for task completion events
   * @param {string} taskId - Task ID to listen for
   * @param {Function} callback - Callback function
   */
  addTaskListener(taskId, callback) {
    if (!this.listeners.has(taskId)) {
      this.listeners.set(taskId, []);
    }
    this.listeners.get(taskId).push(callback);
  }

  /**
   * Remove task listener
   * @param {string} taskId - Task ID
   * @param {Function} callback - Callback function to remove
   */
  removeTaskListener(taskId, callback) {
    if (this.listeners.has(taskId)) {
      const callbacks = this.listeners.get(taskId);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        this.listeners.delete(taskId);
      }
    }
  }

  /**
   * Handle messages from service worker
   * @param {MessageEvent} event
   */
  handleServiceWorkerMessage(event) {
    const { type, taskId, result, error } = event.data;

    switch (type) {
      case 'TASK_COMPLETED':
        logger.log('✅ Task completed:', taskId);
        this.notifyListeners(taskId, { status: 'completed', result });
        break;

      case 'TASK_FAILED':
        logger.log('❌ Task failed:', taskId, error);
        this.notifyListeners(taskId, { status: 'failed', error });
        break;

      case 'TASK_PROCESSING':
        logger.log('⏳ Task processing:', taskId);
        this.notifyListeners(taskId, { status: 'processing' });
        break;

      default:
        logger.log('📨 Unknown service worker message:', event.data);
    }
  }

  /**
   * Notify all listeners for a task
   * @param {string} taskId
   * @param {Object} data
   */
  notifyListeners(taskId, data) {
    if (this.listeners.has(taskId)) {
      this.listeners.get(taskId).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error('Error in task listener:', error);
        }
      });
    }
  }

  /**
   * Check if background sync is supported
   * @returns {boolean}
   */
  isBackgroundSyncSupported() {
    return 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype;
  }

  /**
   * Get database statistics
   * @returns {Promise<Object>} Stats object
   */
  async getStats() {
    return await pixleyDB.getStats();
  }

  /**
   * Clean up old completed tasks
   * @param {number} maxAge - Max age in milliseconds
   * @returns {Promise<number>} Number of tasks cleaned
   */
  async cleanup(maxAge) {
    return await pixleyDB.cleanupOldTasks(maxAge);
  }

  /**
   * Cancel a pending task
   * @param {string} taskId - Task ID to cancel
   * @returns {Promise<void>}
   */
  async cancelTask(taskId) {
    await pixleyDB.updateTaskStatus(taskId, 'cancelled');
    logger.log('🚫 Task cancelled:', taskId);
  }

  /**
   * Retry a failed task
   * @param {string} taskId - Task ID to retry
   * @returns {Promise<void>}
   */
  async retryTask(taskId) {
    const task = await pixleyDB.getPendingTask(taskId);
    if (!task) {
      throw new Error('Task not found');
    }

    if (task.status !== 'failed') {
      throw new Error('Can only retry failed tasks');
    }

    // Reset task to pending status
    await pixleyDB.updateTaskStatus(taskId, 'pending');

    // Re-register background sync
    if (this.registration && 'sync' in this.registration) {
      await this.registration.sync.register(taskId);
      logger.log('🔄 Task retry registered:', taskId);
    }
  }
}

// Create singleton instance
const backgroundTaskManager = new BackgroundTaskManager();

export default backgroundTaskManager;

// Named exports for testing
export { BackgroundTaskManager };